import { useState, useEffect } from "react";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";
import { Users, Eye, ArrowRight, Store, RotateCw, TrendingUp } from "lucide-react";

export function AdminAnalyticsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchAnalytics = async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics?days=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const funnelData = data?.funnel || {};
  const convRate = funnelData.home_visitors > 0
    ? ((funnelData.quote_submitted / funnelData.home_visitors) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Period selector */}
      <div className="flex items-end gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Period</Label>
          <div className="flex gap-2 mt-1">
            {[7, 14, 30, 90].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? "default" : "outline"}
                onClick={() => { setDays(d); fetchAnalytics(d); }}
                data-testid={`analytics-period-${d}`}
              >
                {d}d
              </Button>
            ))}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => fetchAnalytics(days)} data-testid="analytics-refresh">
          <RotateCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <Eye className="h-5 w-5 mx-auto mb-1 text-blue-500" />
            <p className="text-3xl font-bold" data-testid="analytics-total-views">{data?.total_views?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Page Views</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 mx-auto mb-1 text-green-500" />
            <p className="text-3xl font-bold" data-testid="analytics-total-visitors">{data?.total_visitors?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Unique Visitors</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <RotateCw className="h-5 w-5 mx-auto mb-1 text-amber-500" />
            <p className="text-3xl font-bold" data-testid="analytics-returning">{data?.returning_visitors || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Returning Visitors</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <Store className="h-5 w-5 mx-auto mb-1 text-purple-500" />
            <p className="text-3xl font-bold" data-testid="analytics-shop-logins">{data?.shop_logins || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Shop Logins</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-3xl font-bold text-primary" data-testid="analytics-conversion">{convRate}%</p>
            <p className="text-xs text-muted-foreground mt-1">Visitor → Quote</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Traffic Chart */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Daily Traffic</CardTitle>
          <CardDescription>Page views and unique visitors per day</CardDescription>
        </CardHeader>
        <CardContent>
          {data?.daily?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis fontSize={11} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, fontSize: 13 }}
                  labelFormatter={(d) => `Date: ${d}`}
                />
                <Area
                  type="monotone"
                  dataKey="views"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary))"
                  fillOpacity={0.15}
                  strokeWidth={2}
                  name="Page Views"
                />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  stroke="hsl(var(--chart-2))"
                  fill="hsl(var(--chart-2))"
                  fillOpacity={0.1}
                  strokeWidth={2}
                  name="Unique Visitors"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground py-8">No traffic data yet. Views will appear as visitors browse the site.</p>
          )}
        </CardContent>
      </Card>

      {/* Funnel + Top Pages side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Conversion Funnel</CardTitle>
            <CardDescription>Visitor journey from homepage to quote submission</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <FunnelStep
                label="Homepage Visitors"
                count={funnelData.home_visitors || 0}
                total={data?.total_visitors || 1}
                color="bg-blue-500"
              />
              <div className="flex justify-center">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <FunnelStep
                label="Viewed Quote Request Page"
                count={funnelData.quote_page_visitors || 0}
                total={data?.total_visitors || 1}
                color="bg-amber-500"
              />
              <div className="flex justify-center">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <FunnelStep
                label="Submitted a Quote Request"
                count={funnelData.quote_submitted || 0}
                total={data?.total_visitors || 1}
                color="bg-green-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Top Pages</CardTitle>
            <CardDescription>Most visited pages in this period</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.top_pages?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_pages.map((p, i) => (
                    <TableRow key={p.page}>
                      <TableCell className="font-medium text-sm">
                        {p.page || "/"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">{p.views}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No page data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New vs Returning */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Visitor Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-8 items-center">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>New Visitors</span>
                <span className="font-semibold">{data?.new_visitors || 0}</span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-700"
                  style={{ width: `${data?.total_visitors ? (data.new_visitors / data.total_visitors * 100) : 0}%` }}
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Returning Visitors</span>
                <span className="font-semibold">{data?.returning_visitors || 0}</span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all duration-700"
                  style={{ width: `${data?.total_visitors ? (data.returning_visitors / data.total_visitors * 100) : 0}%` }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FunnelStep({ label, count, total, color }) {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">{count} ({pct}%)</span>
      </div>
      <div className="h-4 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all duration-700`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </div>
  );
}
